import { financialItemPath } from 'src/financial/financial-data/financial-item/financialItemPaths';
import { Anchor, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { api } from 'src/common/utils/api';
import { Button } from 'src/design-system/Button';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  OwnerRowValue,
  productSupportsOwners,
} from 'src/financial/financial-data/financial-item/forms/common/OwnersEditor';
import {
  addedCoOwnerIds,
  CoOwnerCopy,
  describeLikelyCopy,
  LikelyCopy,
  SavedOntoCopy,
} from 'src/financial/financial-data/financial-item/likelyCopies';
import { useOwnerNames } from 'src/financial/financial-data/hooks/useOwnerNames';
import {
  useFamilyMembers,
  type FamilyMember,
} from 'src/non-financial-data/v2/family/useFamilyMembers';
import { GenericEntity } from 'src/data-capture/types';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';

type Choice = 'same' | 'separate';

/**
 * What "they are the same" does. A save keeps the item being saved and folds the co-owner's copy
 * into it. Creating a connected asset makes an item that holds only its reference until the feed
 * fills it in, so that path records the co-owner's existing copy for the new owners instead.
 * Adding an item from document review has no item yet either, but holds the document's values,
 * so that path saves them onto the co-owner's copy.
 */
type SameMeans = 'fold-theirs' | 'use-theirs' | 'save-onto-theirs';

const capitaliseFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

type CopyGroup = { label?: string; copies: CoOwnerCopy[] };

type PendingChoice = {
  groups: CopyGroup[];
  sameMeans: SameMeans;
  resolve: (choices: Record<string, Choice> | null) => void;
};

type SaveToCheck = {
  /** The entity type being saved, e.g. 'InvestmentAndCash'. */
  product: string;
  clientId: string;
  itemId?: string;
  /** The fields the save is about to send, owners included. */
  fields: GenericEntity;
  storedOwners?: OwnerRowValue[];
  /** Names the item being saved, where one check covers several saves. */
  label?: string;
  /** Items the adviser has already been shown against this save, which are not asked about again. */
  shownItemIds?: string[];
};

const choiceKey = (groupIndex: number, copy: CoOwnerCopy) => `${groupIndex}:${copy.item_id}`;

// Only folding deletes the co-owner's copy. Adding the new owners to it, or saving the document
// onto it, keeps its history, but saving onto it sets its owners, so a copy another client also
// owns would drop them.
const canBeTheSame = (copy: CoOwnerCopy, sameMeans: SameMeans) => {
  if (sameMeans === 'use-theirs') return true;
  if (sameMeans === 'save-onto-theirs') return copy.fold_blocker !== 'COPY_OWNED_BY_ANOTHER_CLIENT';
  return copy.fold_blocker === null;
};

const sameLabel = (sameMeans: SameMeans, coOwnerName: string) => {
  if (sameMeans === 'use-theirs') {
    return `They are the same: use ${coOwnerName}'s copy and add the other owners to it`;
  }
  if (sameMeans === 'save-onto-theirs') {
    return `They are the same: update ${coOwnerName}'s copy from this document`;
  }
  return `They are the same: keep this one and delete ${coOwnerName}'s copy`;
};

const whyNotTheSame = (copy: CoOwnerCopy, sameMeans: SameMeans) => {
  if (copy.fold_blocker === 'COPY_HAS_HISTORY') {
    return `${capitaliseFirst(copy.coOwnerName)}'s copy has its own history, such as holdings, fees, notes or a feed connection, so it cannot be folded into this one.`;
  }
  if (sameMeans === 'save-onto-theirs') {
    return `Someone else also owns ${copy.coOwnerName}'s copy, so it cannot be updated from this document.`;
  }
  return `Someone else also owns ${copy.coOwnerName}'s copy, so it cannot be folded into this one.`;
};

const separateLabel: Record<SameMeans, string> = {
  'fold-theirs': 'They are separate: keep both',
  'use-theirs': 'They are separate: create a new one',
  'save-onto-theirs': 'They are separate: add a new one',
};

/**
 * With item owners on, a client sees every item they own. Adding a client as an owner of an item
 * they already hold a record of would show the holding on their Financial tab twice, so before
 * such a save `chooseSameCopies` asks the adviser, for each likely copy, whether it is the same
 * holding. It resolves to the copies the adviser says are the same, or null when they go back to
 * the form; `chooseSameCopiesForEach` asks once for several saves. `foldCopies` then folds those
 * into the saved item, `addOwnersToCopy` records one for the new owners in place of creating
 * another, or `saveOntoCopy` records a detected item on one.
 */
export function useLikelyCopyCheck({
  members: providedMembers,
}: { members?: FamilyMember[] } = {}) {
  const { flagEnabledFinancialItemOwners } = useFlags();
  // `useFamilyMembers` resolves the family from the current client, and the family route has none,
  // so callers there pass the roster they already hold.
  const { members: resolvedMembers } = useFamilyMembers({
    enabled: !!flagEnabledFinancialItemOwners && providedMembers === undefined,
  });
  const members = providedMembers ?? resolvedMembers;
  const ownerName = useOwnerNames(members);
  const currency = useCurrency();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingChoice | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});

  // The fallback only covers names still loading.
  const coOwnerName = (clientId: string) => ownerName(clientId) ?? 'the new owner';

  const findCopies = async (coOwnerId: string, save: SaveToCheck): Promise<CoOwnerCopy[]> => {
    const { data } = await api.post<LikelyCopy[]>(
      `clients/${coOwnerId}/financial-items/likely-copies`,
      { entity_type: save.product, item_id: save.itemId ?? null, fields: save.fields },
    );
    return data.map(copy => ({ ...copy, coOwnerId, coOwnerName: coOwnerName(coOwnerId) }));
  };

  const findCopiesFor = async (save: SaveToCheck): Promise<CoOwnerCopy[]> => {
    if (!productSupportsOwners(save.product)) return [];
    const coOwnerIds = addedCoOwnerIds(
      save.fields.owners as OwnerRowValue[] | undefined,
      save.storedOwners,
      save.clientId,
    );
    const shown = new Set(save.shownItemIds ?? []);
    return (await Promise.all(coOwnerIds.map(coOwnerId => findCopies(coOwnerId, save))))
      .flat()
      .filter(copy => !shown.has(copy.item_id));
  };

  const chooseSameCopiesForEach = async (
    saves: SaveToCheck[],
    sameMeans: SameMeans = 'fold-theirs',
  ): Promise<CoOwnerCopy[][] | null> => {
    const noneTheSame = saves.map((): CoOwnerCopy[] => []);
    if (!flagEnabledFinancialItemOwners) return noneTheSame;

    let found: CoOwnerCopy[][];
    try {
      found = await Promise.all(saves.map(findCopiesFor));
    } catch (error) {
      // A failed check must not stop the adviser saving their work.
      Logger.warn('Likely copy check failed', { error });
      return noneTheSame;
    }
    if (found.every(copies => copies.length === 0)) return noneTheSame;

    setChoices({});
    const groups = found.map((copies, index) => ({ label: saves[index].label, copies }));
    const chosen = await new Promise<Record<string, Choice> | null>(resolve =>
      setPending({ groups, sameMeans, resolve }),
    );
    setPending(null);
    if (chosen === null) return null;
    return found.map((copies, groupIndex) =>
      copies.filter(copy => chosen[choiceKey(groupIndex, copy)] === 'same'),
    );
  };

  const chooseSameCopies = async (
    save: SaveToCheck,
    sameMeans: SameMeans = 'fold-theirs',
  ): Promise<CoOwnerCopy[] | null> => {
    const chosen = await chooseSameCopiesForEach([save], sameMeans);
    return chosen === null ? null : chosen[0];
  };

  // Runs after the save, so the co-owner already owns the kept item and the copy is the only
  // record that goes. Folding can take the copy's newer value onto this item, so the page's own
  // client is refetched. Resolves to the copies that were folded.
  const foldCopies = async (
    clientId: string,
    keptItemId: string,
    copies: CoOwnerCopy[],
  ): Promise<CoOwnerCopy[]> => {
    if (copies.length === 0) return [];
    const folded: CoOwnerCopy[] = [];
    try {
      for (const copy of copies) {
        await api.post(`clients/${copy.coOwnerId}/financial-items/fold-copy`, {
          entity_type: copy.entity_type,
          kept_item_id: keptItemId,
          copy_item_id: copy.item_id,
        });
        folded.push(copy);
      }
    } catch (error) {
      notifyAndLogError(
        'Copies not folded together',
        "This item was saved, but the other copy is still there. Open it from its owner's Financial tab to delete it.",
        error,
        { keptItemId },
      );
    }
    await queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
    return folded;
  };

  const addOwnersToCopy = async (copy: CoOwnerCopy, ownerClientIds: string[]) => {
    await api.post(`clients/${copy.coOwnerId}/financial-items/add-owners`, {
      entity_type: copy.entity_type,
      item_id: copy.item_id,
      owner_client_ids: ownerClientIds,
    });
  };

  // Through the co-owner, who owns the copy. The save names the detected item's owners, so the
  // copy joins the other owners' Financial tabs.
  const saveOntoCopy = async (copy: CoOwnerCopy, fields: GenericEntity) => {
    const { data } = await api.post<SavedOntoCopy>(
      `clients/${copy.coOwnerId}/financial-items/save-onto-copy`,
      { entity_type: copy.entity_type, copy_item_id: copy.item_id, fields },
    );
    return data;
  };

  const sameMeans = pending?.sameMeans ?? 'fold-theirs';
  const groupsWithCopies = (pending?.groups ?? [])
    .map((group, groupIndex) => ({ ...group, groupIndex }))
    .filter(group => group.copies.length > 0);
  const choosableKeys = groupsWithCopies.flatMap(group =>
    group.copies
      .filter(copy => canBeTheSame(copy, sameMeans))
      .map(copy => choiceKey(group.groupIndex, copy)),
  );
  const everyChoiceMade = choosableKeys.every(key => choices[key] !== undefined);

  // A copy can be the same as only one of the items being saved. Saving onto a copy folds every
  // other copy said to be the same into it, so only one that cannot be folded can be chosen.
  const whySameIsTaken = (groupIndex: number, copy: CoOwnerCopy): string | null => {
    const chosenElsewhere = groupsWithCopies.flatMap(group =>
      group.copies
        .filter(other => choiceKey(group.groupIndex, other) !== choiceKey(groupIndex, copy))
        .filter(other => choices[choiceKey(group.groupIndex, other)] === 'same')
        .map(other => ({ other, groupIndex: group.groupIndex })),
    );
    if (chosenElsewhere.some(({ other }) => other.item_id === copy.item_id)) {
      return 'This copy is already the same as another item above.';
    }
    const anotherCannotBeFolded = chosenElsewhere.some(
      ({ other, groupIndex: otherGroupIndex }) =>
        otherGroupIndex === groupIndex && other.fold_blocker !== null,
    );
    if (sameMeans === 'save-onto-theirs' && copy.fold_blocker !== null && anotherCannotBeFolded) {
      return 'This copy has its own history, so it cannot be folded into the copy already taking this document.';
    }
    return null;
  };
  const showsGroupLabels = (pending?.groups.length ?? 0) > 1;

  const likelyCopiesModal = (
    <Modal
      opened={pending !== null}
      onClose={() => pending?.resolve(null)}
      title="This may already be recorded"
      centered
      size="lg"
    >
      <Stack gap="lg">
        {groupsWithCopies.map(group => (
          <Stack key={group.groupIndex} gap="md">
            {showsGroupLabels && group.label && (
              <Text size="sm" fw={700} c="gray.10">
                {group.label}
              </Text>
            )}
            {group.copies.map(copy => {
              const key = choiceKey(group.groupIndex, copy);
              const sameIsTaken = whySameIsTaken(group.groupIndex, copy);
              return (
                <Stack key={key} gap="xs">
                  <Text size="sm" c="gray.10">
                    {capitaliseFirst(copy.coOwnerName)} holds {describeLikelyCopy(copy, currency)},
                    which may be the same one.
                  </Text>
                  {canBeTheSame(copy, sameMeans) ? (
                    <Radio.Group
                      value={choices[key] ?? null}
                      onChange={value =>
                        setChoices(current => ({ ...current, [key]: value as Choice }))
                      }
                    >
                      <Stack gap="xs">
                        <Radio
                          value="same"
                          label={sameLabel(sameMeans, copy.coOwnerName)}
                          disabled={sameIsTaken !== null}
                          description={sameIsTaken}
                        />
                        <Radio value="separate" label={separateLabel[sameMeans]} />
                      </Stack>
                    </Radio.Group>
                  ) : (
                    <Text size="sm" c="gray.7">
                      {whyNotTheSame(copy, sameMeans)}{' '}
                      <Anchor
                        href={financialItemPath(copy.coOwnerId, copy.entity_type, copy.item_id)}
                        target="_blank"
                        size="sm"
                      >
                        Open their copy
                      </Anchor>
                    </Text>
                  )}
                </Stack>
              );
            })}
          </Stack>
        ))}
        {sameMeans === 'fold-theirs' && choosableKeys.length > 0 && (
          <Text size="xs" c="gray.7">
            Deleting a copy cannot be undone. If it was valued more recently, its value is kept on
            this one.
          </Text>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="secondary" onClick={() => pending?.resolve(null)}>
            Go back
          </Button>
          <Button disabled={!everyChoiceMade} onClick={() => pending?.resolve(choices)}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  return {
    chooseSameCopies,
    chooseSameCopiesForEach,
    foldCopies,
    addOwnersToCopy,
    saveOntoCopy,
    likelyCopiesModal,
  };
}
